import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import DataTable from '../../components/DataTable';
import DialogShell from '../../components/DialogShell';
import {
  listProxies,
  addProxy,
  setProxyErrorCount,
  removeProxy,
  rebalanceProxylessAccounts,
  unassignProxyAccounts,
} from '../../services/cloneProvider';
import { getErrorMessage } from './badges';

function PasswordCell({ value }) {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <button
      type="button"
      onClick={() => setRevealed((v) => !v)}
      className="inline-flex items-center gap-1 font-mono text-xs text-slate-700 hover:text-cyan-700"
    >
      {revealed ? value : '••••••••'}
      {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
    </button>
  );
}

function AddProxyDialog({ onClose, onDone }) {
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await addProxy({ host, port: Number(port), username: username || undefined, password: password || undefined });
      toast.success('Đã thêm proxy.');
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <div className="w-96 max-w-full">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Thêm proxy</h3>
        <p className="mb-3 text-xs text-slate-500">
          Proxy mới không tự gán cho account nào ngay. Thêm xong hết các proxy muốn thêm rồi bấm "Chia proxy
          cho account thiếu" để chia đều account chưa có proxy qua các proxy hiện có.
        </p>
        <form onSubmit={handleSubmit} className="space-y-2">
          <input
            required
            placeholder="host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            required
            type="number"
            placeholder="port"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            placeholder="username (tuỳ chọn)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            placeholder="password (tuỳ chọn)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Đang thêm...' : 'Thêm proxy'}
          </button>
        </form>
      </div>
    </DialogShell>
  );
}

function ErrorCountCell({ host, port, value, onSaved }) {
  const normalizedValue = String(value);
  const [draft, setDraft] = useState(normalizedValue);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setDraft(normalizedValue), [normalizedValue]);

  async function handleSave() {
    setIsSaving(true);
    try {
      await setProxyErrorCount(host, port, Number(draft));
      toast.success('Đã lưu error_count.');
      onSaved();
    } catch (error) {
      toast.error(getErrorMessage(error));
      setDraft(normalizedValue);
    } finally {
      setIsSaving(false);
    }
  }

  const isValid = Number.isInteger(Number(draft)) && Number(draft) >= 0;

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm"
      />
      <button
        type="button"
        disabled={isSaving || !isValid || draft === normalizedValue}
        onClick={handleSave}
        className="rounded-lg bg-cyan-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-700 disabled:opacity-40"
      >
        Lưu
      </button>
    </div>
  );
}

const PAGE_SIZE = 50;

export default function ProxiesTab() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [isRebalancing, setIsRebalancing] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listProxies();
      setItems(data.items);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleRebalance() {
    setIsRebalancing(true);
    try {
      const data = await rebalanceProxylessAccounts();
      toast.success(`Đã gán proxy cho ${data.count} account.`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRebalancing(false);
    }
  }

  async function handleRemove(host, port) {
    const target = items.find((p) => p.host === host && p.port === port);
    const warning =
      target?.account_count > 0
        ? `${target.account_count} account đang dùng proxy này sẽ được tự động gán proxy khác (hoặc bỏ proxy nếu không còn proxy nào). `
        : '';
    if (!window.confirm(`${warning}Xoá proxy ${host}:${port}?`)) return;

    setBusyKey(`${host}:${port}`);
    try {
      await removeProxy(host, port);
      toast.success('Đã xoá proxy.');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleUnassign(host, port) {
    const target = items.find((p) => p.host === host && p.port === port);
    if (!target?.account_count) {
      toast.error('Không có account nào đang dùng proxy này.');
      return;
    }
    if (
      !window.confirm(
        `Gỡ proxy khỏi ${target.account_count} account đang dùng ${host}:${port}? Các account này sẽ về trạng thái không có proxy (không tự gán proxy khác).`
      )
    )
      return;

    setBusyKey(`unassign:${host}:${port}`);
    try {
      const data = await unassignProxyAccounts(host, port);
      toast.success(`Đã gỡ proxy khỏi ${data.count} account.`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  }

  const columns = [
    { key: 'host', label: 'Host' },
    { key: 'port', label: 'Port' },
    { key: 'username', label: 'Username', render: (row) => row.username || <span className="text-slate-400">—</span> },
    { key: 'password', label: 'Password', render: (row) => <PasswordCell value={row.password} /> },
    {
      key: 'error_count',
      label: 'Error count',
      render: (row) => (
        <ErrorCountCell host={row.host} port={row.port} value={row.error_count} onSaved={fetchData} />
      ),
    },
    { key: 'account_count', label: 'Accounts đang dùng' },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex gap-3">
          <button
            type="button"
            disabled={busyKey === `unassign:${row.host}:${row.port}`}
            onClick={() => handleUnassign(row.host, row.port)}
            className="text-xs font-medium text-amber-600 hover:underline disabled:opacity-40"
          >
            Gỡ tài khoản
          </button>
          <button
            type="button"
            disabled={busyKey === `${row.host}:${row.port}`}
            onClick={() => handleRemove(row.host, row.port)}
            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-40"
          >
            Xoá
          </button>
        </div>
      ),
    },
  ];

  const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2 rounded-xl border border-cyan-100 bg-white p-3">
        <button
          type="button"
          onClick={handleRebalance}
          disabled={isRebalancing}
          title="Gán proxy cho mọi account đang chưa có proxy, chia đều qua các proxy hiện có"
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {isRebalancing ? 'Đang chia...' : 'Chia proxy cho account thiếu'}
        </button>
        <button
          type="button"
          onClick={() => setShowAddDialog(true)}
          className="rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-700"
        >
          + Thêm proxy
        </button>
      </div>

      <DataTable
        columns={columns}
        data={pagedItems}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={items.length}
        onPageChange={setPage}
      />

      {showAddDialog && (
        <AddProxyDialog
          onClose={() => setShowAddDialog(false)}
          onDone={() => {
            setShowAddDialog(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
